import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader, Mail, AlertTriangle, Check, RefreshCw, X, Users, Shuffle, Search } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import { logVipOutreach, neonQuery } from '../services/api';
import { buildEml, downloadEml, slugName, newSendToken, trackingReady, pixelTag } from '../lib/outreachEmail';
import { buildWinbackEmail } from '../lib/winbackEmail';
import { draftIntoOutlook } from '../lib/outreachDraft';
import { OFFER_TERMS } from '../lib/winbackTerms';
import OutreachDeliverability from './OutreachDeliverability';
import { DncModal } from './DoNotContact';
import VipWinbackFollowup from './VipWinbackFollowup';

// VIP WIN-BACK - the queue Rachael's team works.
//
// Greg, 2026-09-14: "let's just do it the way we've been doing it. The girls will generate them,
// and the girls will get the responses and kinda help people back on."
//
// ⚠️ FIFTY EACH, NOT FIFTY BETWEEN THEM. Greg, 2026-09-15: "I'd like to do fifty for Ashley,
// fifty for Linda, and fifty for Rachael ... the goal is to send a total of a hundred and fifty
// emails." The first build counted one shared fifty, so the first two reps to sit down took the
// third's allowance and she opened the tab to a hard stop she had not caused. The cap is now per
// rep, counted against the SIGNED-IN admin server side, and 150 is arithmetic rather than a
// second number somebody has to keep in step.
//
// ⚠️ NOBODY NEEDS A BATCH DEALT TO THEM TO WORK THIS SCREEN. Assignment is how Rachael divides
// the list and covers for somebody who is out, not a gate. An unassigned customer is anybody's,
// which is why "Unassigned" is a real view and is what a rep with no batch lands on.
//
// ⚠️ THE FIRST SEGMENT IS 819 PEOPLE WHOSE CARD FAILED, NOT 16,572 LAPSED MEMBERS. They did not
// decide to leave. 43% of them failed on `E00040`, which is our own lookup bug rather than
// anything wrong with their card. That is why the offer is free and why this segment leads.
//
// ⚠️ TWO SEGMENTS CANNOT BE EMAILED, AND THE SERVER ENFORCES BOTH.
// "Paid, but showing lapsed" is 25 members charged in the 31 March batch whose expiry never
// moved. "Expiry not on 31 March" is ~320 whose expiry moved into April, most of whom paid, and
// who are most likely cancellations rather than people we failed. Both are shown so somebody
// works out what they are, never so they get mailed on a guess.

const money = (n) => '$' + Math.round(Number(n || 0)).toLocaleString();
const fmtDate = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || 'rep';

async function callWinback(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base + '/vip-winback', { method: 'POST', headers, body: JSON.stringify({ action, ...params }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'winback ' + res.status);
  return res.json();
}

// Greg's number: a rep's day is 50 win-backs. The server takes 60 in a request, so this
// sits under that and a longer queue is simply not drafted in one press.
const DRAFT_BATCH = 50;

const BTN = (bg, border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
  fontSize: 12, fontWeight: 600, background: bg, border: '1px solid ' + border, color,
  cursor: 'pointer', fontFamily: 'inherit',
});

// ⚠️ WHAT THE REP IS TOLD ABOUT WHY THEY LAPSED IS NOT WHAT THE CUSTOMER IS TOLD, AND THE BANNER
// SAYS SO. The email deliberately never mentions a payment (Greg, 2026-09-15: "it makes them
// sound like a deadbeat"), and it could not honestly do so anyway - 421 of these people had
// auto-renew switched off and were never charged at all, so "your card failed" describes
// something that never happened to them. The old banner said "their card failed" for every row.
// 2026-09-30: auto-renew-off is finished; Greg moved the team on to "Expiry not on 31 March",
// whose refunded members now land in the refunded group (see LAPSED_CTE).
const DEFAULT_2026_REASON = 'refunded';

const WHY = {
  card_failed: (code) => 'Their card declined in the 31 March renewal batch'
    + (code ? ' (' + code + ')' : '') + ', so they did not choose to leave.',
  never_attempted: () => 'Auto-renew was switched off on their account, so nothing was ever charged.',
  unknown: () => 'They lapsed before our renewal records begin, so we cannot say why.',
};

// ── The composer ────────────────────────────────────────────────────────────────────────────
// Same shape as the existing VIP offer modal: editable body, opens in Outlook from the rep's own
// account, logs the send. The rep's edits travel into the .eml because the body is read back out
// of the contenteditable at send time rather than from state.
function WinbackModal({ customer, rep, onClose, onSent }) {
  const built = useMemo(() => buildWinbackEmail(customer, rep), [customer, rep]);
  const [to, setTo] = useState(built.to);
  const [subject, setSubject] = useState(built.subject);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState('');
  const bodyRef = useRef(null);

  useEffect(() => { if (bodyRef.current) bodyRef.current.innerHTML = built.html; }, [built]);
  // Ask whether the tracking host answers as soon as the composer opens, so the answer is already
  // in hand when she clicks and the download is not held up behind a network check.
  useEffect(() => { trackingReady(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ⚠️ THE LOG IS THE CLAIM, AND IT RUNS BEFORE THE .eml IS BUILT. It used to run after the
  // download, which meant a refusal arrived when the draft was already sitting in Outlook - and
  // that is how 50 customers got this offer twice on 2026-09-16. The server refuses a duplicate
  // (vip_outreach_add), so a blocked claim means nothing was written and nothing was handed to
  // Outlook. Returns the refusal rather than throwing, because it is not an error.
  const claim = async (track) => {
    const res = await logVipOutreach({
      customerId: customer.customerId, email: to, orderNumber: null,
      wouldHaveSaved: 0, offerPrice: OFFER_TERMS.price,
      adminId: rep.adminId, repName: rep.name, repEmail: rep.email,
      rangeStart: null, rangeEnd: null,
      // mode and category are what separate win-back sends from the delivered-by-day sends in
      // the same table, and what the per-segment win rate is grouped by.
      mode: 'winback',
      category: customer.segmentKey,
      // Only a send that actually carried the pixel counts toward an open rate.
      sendToken: track ? track.token : null,
      pixelEmbedded: !!(track && track.embedded),
    });
    if (res && res.blocked) return res;
    onSent(customer.customerId);
    return null;
  };

  const openInOutlook = async () => {
    setBusy(true);
    const token = newSendToken();
    const embedded = await trackingReady();
    let blocked;
    try { blocked = await claim({ token, embedded }); }
    catch (_) {
      setWarn('We could not record this send, so the email has not been opened. Try again in a moment.');
      setBusy(false);
      return;
    }
    if (blocked) { setWarn(blocked.reason); setBusy(false); return; }
    const body = bodyRef.current ? bodyRef.current.innerHTML : built.html;
    const html = embedded ? body + pixelTag(token) : body;
    const eml = buildEml({ fromEmail: rep.email || 'customerservice@crabplace.com', fromName: rep.name, to, subject, html });
    downloadEml(`winback-${slugName(customer.name)}.eml`, eml);
    onClose();
  };

  // Sent some other way, so no pixel went with it and it must not count toward an open rate.
  const markOnly = async () => {
    setBusy(true);
    try {
      const blocked = await claim(null);
      if (blocked) { setWarn(blocked.reason); setBusy(false); return; }
      onClose();
    } catch (_) { setBusy(false); }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.55)', zIndex: 4000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' };
  const panel = { width: '100%', maxWidth: 720, background: '#fff', borderRadius: 12, boxShadow: '0 20px 48px rgba(16,24,40,0.28)', overflow: 'hidden' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderBottom: '1px solid #f2f4f7' };
  const lbl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', width: 56, flexShrink: 0 };
  const inp = { flex: 1, border: 'none', outline: 'none', fontSize: 14, fontFamily: 'inherit', color: 'var(--gray-900)' };
  const why = (WHY[customer.reason] || (() => ''))(customer.errorCode);

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={panel}>
        <div style={{ padding: '14px 18px', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mail size={16} />
          <div style={{ fontWeight: 700, fontSize: 15 }}>Win-back offer to {customer.name}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '10px 18px', background: '#fff8f3', borderBottom: '1px solid #f0c9b4', fontSize: 12.5, color: 'var(--gray-700)' }}>
          Free through <b>{OFFER_TERMS.freeThroughLabel}</b>, then renews at <b>${OFFER_TERMS.renewalPrice.toFixed(2)}</b> on {OFFER_TERMS.renewsOnLabel}.
          {why ? ' ' + why : ''}
          {' '}<span style={{ color: 'var(--gray-500)' }}>The email does not raise any of that, on purpose. Please do not add it.</span>
        </div>

        {/* ⚠️ WHO IT GOES OUT AS, SHOWN BEFORE SHE SENDS IT. Greg, 2026-09-15: "make sure that
            nobody gets a CPx, because that's not going to make any sense at all." They do not:
            the .eml carries X-Unsent, so Outlook composes it from the rep's own mailbox. But
            `rep.email` is resolved from adm_users AFTER the page loads, and the builder falls
            back to customerservice@ if that lookup has not landed, so the one case that could
            put a shared address in front of a customer is a slow or failed lookup. It is named
            here rather than left to be discovered in somebody's Sent Items. */}
        <div style={row}>
          <span style={lbl}>From</span>
          <span style={{ ...inp, color: rep.email ? 'var(--gray-700)' : '#a12020' }}>
            {rep.email
              ? `${rep.name} <${rep.email}>`
              : 'We could not look up your email address. Refresh the page before sending, or this will go out as customerservice@crabplace.com.'}
          </span>
        </div>
        <div style={row}><span style={lbl}>To</span><input style={inp} value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div style={row}><span style={lbl}>Subject</span><input style={inp} value={subject} onChange={(e) => setSubject(e.target.value)} /></div>

        <div ref={bodyRef} contentEditable suppressContentEditableWarning
          style={{ padding: 18, minHeight: 260, maxHeight: '46vh', overflowY: 'auto', outline: 'none', fontSize: 14 }} />

        {warn && (
          <div style={{ padding: '10px 18px', background: '#fff5f5', color: '#a12020', fontSize: 12.5, borderTop: '1px solid #fbb' }}>{warn}</div>
        )}

        <div style={{ padding: '12px 18px', borderTop: '1px solid #f2f4f7', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button disabled={busy || !to} onClick={openInOutlook} style={{ ...BTN('var(--orange)', 'var(--orange)', '#fff'), padding: '9px 16px', fontSize: 13, opacity: busy || !to ? 0.6 : 1 }}>
            <Mail size={14} /> Open in Outlook
          </button>
          <button disabled={busy} onClick={markOnly} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>Mark as emailed</button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>Edit anything above before you send it.</span>
        </div>
      </div>
    </div>
  );
}

// ── Deal the batch ──────────────────────────────────────────────────────────────────────────
// Rachael's morning: tick who is in today, confirm the numbers, hand it out. The default for
// each rep is the cap MINUS what she is already carrying, so the button tops people up instead
// of piling a fresh fifty on top of yesterday's untouched thirty.
function DealPanel({ reps, team, cap, segment, onClose, onDone }) {
  const openFor = useCallback(
    (id) => (team.find((t) => String(t.adminId) === String(id)) || {}).open || 0, [team]);
  // Pre-tick the people who have actually done this work. `pickableReps` is everybody holding
  // the Customer Service role, which is six people; three of them have never touched the queue.
  const [on, setOn] = useState(() => new Set(
    reps.filter((r) => r.recentSends > 0).slice(0, 3).map((r) => r.adminId)));
  const [counts, setCounts] = useState(() => {
    const o = {};
    reps.forEach((r) => { o[r.adminId] = Math.max(0, cap - openFor(r.adminId)); });
    return o;
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = (id) => setOn((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const total = [...on].reduce((a, id) => a + (Number(counts[id]) || 0), 0);

  const go = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await callWinback('deal', {
        year: segment.year, reason: segment.reason,
        reps: [...on].map((id) => ({ adminId: id, count: Number(counts[id]) || 0 })),
      });
      const parts = (r.dealt || []).map((d) => firstName(d.name) + ' ' + d.count);
      // The queue running dry is a real answer and gets said out loud, rather than quietly
      // dealing fewer than she typed and letting her believe the number on screen.
      const short = r.short > 0
        ? ' Only ' + r.pool + ' left unassigned in this group, so that is ' + r.short + ' short of the ' + r.asked + ' you asked for.'
        : '';
      // The server caps one rep at 200 a deal. Saying so beats her counting the rows later.
      const capped = r.clampedFrom
        ? ' You asked for ' + r.clampedFrom + ', and ' + r.ceiling + ' is the most one person can be handed at once.'
        : '';
      setMsg({ ok: true, text: (parts.length ? parts.join(', ') + '.' : 'Nothing to hand out.') + capped + short });
      onDone && onDone(r.team);
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Could not hand the list out.' });
    }
    setBusy(false);
  };

  const chip = (active) => ({
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
    padding: '5px 12px', borderRadius: 999,
    border: '1px solid ' + (active ? 'var(--orange)' : 'var(--gray-300, #d0d5dd)'),
    background: active ? 'var(--orange)' : '#fff', color: active ? '#fff' : 'var(--navy)',
  });

  return (
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(560px,100%)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Users size={17} />
          <div style={{ fontWeight: 800, fontSize: 15 }}>Hand out today's batch</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '14px 18px', fontSize: 12.5, color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-200)' }}>
          The top of the list by lifetime spend, dealt in turn so nobody gets a run of the best
          names. Untick anybody who is out today and their share stays in the pool.
        </div>

        <div style={{ padding: '10px 18px' }}>
          {reps.map((r) => {
            const active = on.has(r.adminId);
            return (
              <div key={r.adminId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
                <button type="button" onClick={() => toggle(r.adminId)} style={{ ...chip(active), minWidth: 104, justifyContent: 'center' }}>
                  {firstName(r.name)}
                </button>
                <input
                  type="number" min="0" max="200" disabled={!active}
                  value={counts[r.adminId]}
                  onChange={(e) => setCounts((c) => ({ ...c, [r.adminId]: e.target.value }))}
                  style={{ width: 64, padding: '5px 8px', fontSize: 13, fontFamily: 'inherit', borderRadius: 6, border: '1px solid var(--gray-300)', opacity: active ? 1 : 0.4 }}
                />
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                  carrying {openFor(r.adminId)} already
                </span>
              </div>
            );
          })}
        </div>

        {msg && (
          <div style={{ padding: '10px 18px', fontSize: 12.5, background: msg.ok ? '#f0fdf4' : '#fff5f5', color: msg.ok ? '#166534' : '#a12020', borderTop: '1px solid var(--gray-200)' }}>{msg.text}</div>
        )}

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button disabled={busy || !total} onClick={go} style={{ ...BTN('var(--orange)', 'var(--orange)', '#fff'), padding: '9px 16px', fontSize: 13, opacity: busy || !total ? 0.6 : 1 }}>
            {busy ? <Loader size={14} className="spin" /> : <Shuffle size={14} />} Hand out {total}
          </button>
          <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
            {on.size} {on.size === 1 ? 'person' : 'people'} working &middot; {cap} a day each
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Already emailed ─────────────────────────────────────────────────────────────────────────
// Rachael, 2026-09-16: a customer answered "I'm in" and she could not find who it was - the reply
// came from don0366@aol.com, the offer had gone to don0366@aim.com, and the queue hides everybody
// already emailed. Every win-back ever sent, searchable by name, any address, the part before the @,
// customer number or phone, and never blocked by the daily fifty.
function SentLookup() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  const run = useCallback(async (term) => {
    setBusy(true);
    try { const r = await callWinback('sent', { q: term }); setRows(r.sent || []); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { run(''); }, [run]);
  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(v), 300);
  };

  const pill = (bg, color, text) => (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: bg, color, marginRight: 4, whiteSpace: 'nowrap' }}>{text}</span>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ position: 'relative', flex: '0 1 460px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--gray-400)' }} />
          <input autoFocus value={q} onChange={(e) => onChange(e.target.value)}
            placeholder="Name, email, customer # or phone"
            style={{ width: '100%', padding: '8px 10px 8px 30px', fontSize: 13.5, borderRadius: 6, border: '1px solid var(--gray-300)', fontFamily: 'inherit' }} />
        </div>
        {busy && <Loader size={14} className="spin" />}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 12 }}>
        A reply can come from a different address than the one we wrote to. Searching the part before the @ finds both,
        so <b>don0366</b> matches don0366@aim.com and don0366@aol.com.
      </div>
      {err && <div style={{ fontSize: 12.5, color: '#a12020', marginBottom: 8 }}>{err}</div>}
      {rows && rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13.5 }}>
          Nobody we have emailed matches that. Try just the first part of the email, or the last name.
        </div>
      ) : rows && (
        <div style={{ overflowX: 'auto' }}>
          <table className="orders-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                {['Customer', 'Customer #', 'Phone', 'Emailed', 'By', 'Since then'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.customerId + r.sentLabel} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '9px 10px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{r.email}</div>
                  </td>
                  <td style={{ padding: '9px 10px', fontWeight: 600 }}>{r.customerId}</td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{r.phone || ''}</td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{r.sentLabel}</td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)' }}>{firstName(r.sentBy)}</td>
                  <td style={{ padding: '9px 10px' }}>
                    {r.backIn && pill('#e7f6ec', '#166534', 'Back in the club')}
                    {r.replied && pill('#eef4ff', 'var(--navy)', 'Replied')}
                    {r.opened && !r.replied && pill('#f2f4f7', 'var(--gray-600)', 'Opened')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length >= 200 && (
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', padding: 8 }}>Showing the latest 200. Search to narrow it down.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── The panel ───────────────────────────────────────────────────────────────────────────────
export default function VipWinbackPanel({ rep }) {
  const [cohorts, setCohorts] = useState([]);
  const [cap, setCap] = useState(null);
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [reps, setReps] = useState([]);
  const [me, setMe] = useState(null);
  // ⚠️ THE DEFAULT GROUP IS WHICHEVER ONE THE TEAM IS WORKING, AND IT IS MOVED BY HAND. Greg,
  // 2026-09-28: card-failed ran out (701 done), so the reps start on auto-renew-off until that
  // runs out too. Never auto-advance it - a rep whose list quietly changes group underneath her
  // is sending a different audience without knowing it. Greg says when to move it.
  //
  // ⚠️ SINCE 2026-10-02 IT OPENS ON THE GROUP *SHE* LAST CLICKED. Greg: "I really don't care how
  // they manage these if some can be sent from one pill and some from another. I'm more
  // concerned that they get their 50 a day out." Linda was finishing "Charged, then refunded"
  // while Rachael started Lapsed 2025, so one shared default was wrong for somebody either way.
  // Still never auto-advanced: it only ever changes because that person clicked a pill.
  const [sel, setSelRaw] = useState(() => {
    try {
      const v = JSON.parse(window.localStorage.getItem('cpx_winback_sel') || 'null');
      if (v && Number.isFinite(Number(v.year)) && typeof v.reason === 'string') return { year: Number(v.year), reason: v.reason };
    } catch (_) { /* private window or blocked storage - fall back to the default */ }
    return { year: 2026, reason: DEFAULT_2026_REASON };
  });
  const setSel = useCallback((v) => {
    setSelRaw(v);
    try { window.localStorage.setItem('cpx_winback_sel', JSON.stringify(v)); } catch (_) { /* ignore */ }
  }, []);
  const [scope, setScope] = useState(null); // null until we know who is looking
  // Which side of the routing rule is on screen. Default is the free-offer pool; the pill
  // flips to the ones being left to Delivered by day.
  const [held, setHeld] = useState('exclude');
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(false);
  const [err, setErr] = useState(null);
  const [composing, setComposing] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [dealing, setDealing] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [moveMsg, setMoveMsg] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [drafting, setDrafting] = useState(null);   // {done, total} while a batch runs
  const [draftMsg, setDraftMsg] = useState(null);
  const [dncFor, setDncFor] = useState(null);

  const mine = useMemo(
    () => team.find((t) => me && String(t.adminId) === String(me.adminId)) || null,
    [team, me]);

  // ⚠️ FIFTY DRAFTS, STRAIGHT INTO HER OWN OUTLOOK - no file, no import, no Outbox to get
  // stuck in. Greg, 2026-09-21: "just put their 50 drafts in so they're just reading and
  // sending... spot check and send." The .eml download is what lost ~480 of Ashley's offers
  // in August and 10 more on 17 September.
  //
  // ⚠️ IT REUSES THE COMPOSER'S OWN BUILDER AND THE COMPOSER'S OWN CLAIM PAYLOAD. Two copies
  // of either would drift, and the claim ordering (claim first, draft only if it is allowed)
  // is what stops a customer being offered the same thing twice.

  const myOpen = useMemo(
    () => (team.find((t) => me && String(t.adminId) === String(me.adminId)) || {}).open || 0,
    [team, me]);

  const loadCohorts = useCallback(async () => {
    try {
      const r = await callWinback('cohorts');
      setCohorts(r.cohorts || []); setCap(r.cap); setStats(r.stats);
      setTeam(r.team || []); setReps(r.reps || []); setMe(r.me || null);
      setErr(null);
      // ⚠️ THE DEFAULT VIEW DEPENDS ON WHETHER ANYTHING HAS BEEN DEALT YET. A rep who lands on
      // "My batch" before Rachael has handed anything out sees an empty screen and concludes the
      // tool is broken, so she starts on the unassigned pool, which is exactly how this worked
      // before assignment existed. Only computed once, or it would yank the view out from under
      // somebody who had deliberately switched it.
      setScope((cur) => {
        if (cur) return cur;
        if (r.me && r.me.isLead) return 'all';
        const mine = (r.team || []).find((t) => r.me && String(t.adminId) === String(r.me.adminId));
        return mine && mine.open > 0 ? 'mine' : 'unassigned';
      });
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  const loadQueue = useCallback(async (s, sc, hd) => {
    // 'sent' and 'followup' draw their own lists; neither is a slice of this queue.
    if (!sc || sc === 'sent' || sc === 'followup') return;
    setListing(true);
    try {
      const r = await callWinback('list', {
        year: s.year, reason: s.reason, limit: 200, scope: sc, held: hd || 'exclude',
      });
      setQueue(r.queue || []); setCap(r.cap); setErr(null);
    } catch (e) { setErr(e.message); } finally { setListing(false); }
  }, []);

  // ⚠️ BOTH OF THESE MUST SIT BELOW loadQueue AND loadCohorts. They name both in their
  // dependency arrays, and a dependency array is evaluated AT RENDER - so declared above the
  // consts they reference, every render threw a temporal-dead-zone ReferenceError and the whole
  // VIP Win-back tab came up BLANK (2026-09-21). `npm run build` compiles it happily; only
  // opening the page finds it. Same trap as the delivered-by-day handler in DailySummary.js.
  // ⚠️ "NOT WORTH PURSUING" IS A DECISION, AND IT ASKS FOR THE REASON. Greg, 2026-09-21:
  // "we'll need a way to let them manually skip people if they just feel like it's not worth
  // the hassle to continue to try to bring them on as a VIP customer." Ashley's own note on
  // one of her ten was "Cust had nothing but problems" - that judgement had nowhere to live,
  // so the same name came back every morning and she passed it over again.
  //
  // ⚠️ THE REASON IS THE POINT OF THE PROMPT, not paperwork. Without it the next person
  // cannot tell a considered call from an oversight, which is the same argument as the
  // "Not today" box on the frog card. Cancelling the prompt skips nobody.
  //
  // ⚠️ IT IS REVERSIBLE and it never deletes anything - vip_outreach_skips.restored_at puts
  // them straight back in the queue, and the reversed decision stays on file.
  const skipCustomer = useCallback(async (c) => {
    const reason = window.prompt(
      `Stop offering the VIP Club to ${c.name}?

They come off the win-back queue until somebody puts them back. Why? (a few words is plenty)`);
    if (reason === null) return;
    try {
      await neonQuery('vip_outreach_skip', {
        customerId: c.customerId, email: c.email || null, customerName: c.name || null,
        reason, adminId: rep.adminId, repName: rep.name,
      });
      loadQueue(sel, scope, held);
      loadCohorts();
    } catch (e) { setErr(e.message); }
  }, [rep, sel, scope, held, loadQueue, loadCohorts]);

  // ⚠️ A CHARGEBACK IS THE ONE THING AUTHORIZE.NET NEVER TELLS US, so a person records it.
  // Rachael, 2026-09-30: 45 of the 48 "paid, but showing lapsed" were chargebacks that lived
  // only on her spreadsheet. Flagging moves them to their own never-mailed group; marking it won
  // (reversed in our favour) puts them back.
  // ⚠️ THIS WRITES TO PRODUCTION dbo.Chargebacks (lib/chargebacks.js), which stores the disputed
  // AMOUNT and refuses a row without one - so it is asked for, prefilled with the membership price.
  const flagChargeback = useCallback(async (c) => {
    const amountIn = window.prompt(
      `Mark ${c.name} as a chargeback on their membership?

They move to the Chargeback group and can never be sent the free offer. Production stops them being reinstated or renewed.

Disputed amount ($):`, '99.99');
    if (amountIn === null) return;
    const amount = Number(String(amountIn).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) { setErr('Enter the disputed amount, e.g. 99.99.'); return; }
    const note = window.prompt('Any note? (optional)', '');
    if (note === null) return;
    try {
      const res = await neonQuery('vip_chargeback_flag', {
        customerId: c.customerId, email: c.email || null, customerName: c.name || null,
        kind: 'membership', amount, note, adminId: rep.adminId, repName: rep.name,
      });
      if (res && res.flagged === false) { setErr(res.reason || 'The chargeback was not recorded.'); return; }
      loadQueue(sel, scope, held);
      loadCohorts();
    } catch (e) { setErr(e.message); }
  }, [rep, sel, scope, held, loadQueue, loadCohorts]);

  const clearChargeback = useCallback(async (c) => {
    // Marks production's record WON: the bank reversed it in our favour. A chargeback entered on the
    // wrong person is removed in the admin, not here.
    const note = window.prompt(`Mark the chargeback on ${c.name} as won (reversed in our favour)? Any note?`, '');
    if (note === null) return;
    try {
      const res = await neonQuery('vip_chargeback_clear', { customerId: c.customerId, note, repName: rep.name });
      if (res && res.cleared === false) { setErr(res.reason || 'Nothing was changed.'); return; }
      loadQueue(sel, scope, held);
      loadCohorts();
    } catch (e) { setErr(e.message); }
  }, [rep, sel, scope, held, loadQueue, loadCohorts]);

  const draftBatch = useCallback(async (rows) => {
    setDraftMsg(null);
    setDrafting({ done: 0, total: Math.min(DRAFT_BATCH, rows.length) });
    try {
      const res = await draftIntoOutlook(rows.map((c) => {
        const built = buildWinbackEmail(c, rep);
        return {
          key: c.customerId,
          claim: (track) => logVipOutreach({
            customerId: c.customerId, email: built.to, orderNumber: null,
            wouldHaveSaved: 0, offerPrice: OFFER_TERMS.price,
            adminId: rep.adminId, repName: rep.name, repEmail: rep.email,
            rangeStart: null, rangeEnd: null,
            mode: 'winback', category: c.segmentKey,
            sendToken: track.token, pixelEmbedded: track.embedded,
          }),
          build: () => ({ subject: built.subject, html: built.html }),
        };
      }), { target: DRAFT_BATCH, onProgress: (done, total) => setDrafting({ done, total }) });
      setDraftMsg(res);
      loadQueue(sel, scope, held);
      loadCohorts();
    } catch (e) {
      setDraftMsg({ error: e.message });
    } finally { setDrafting(null); }
  }, [rep, sel, scope, held, loadQueue, loadCohorts]);

  // ⚠️ THE BOARD COUNTS CLICKS UNTIL THIS RUNS. CPx logs a send when it hands Outlook the .eml,
  // which is not the same as the email leaving - on 2026-09-18 three of Linda's fifty were logged
  // and never left her mailbox while this strip read a clean 50. So after the page has rendered we
  // ask Exchange what it actually holds, then re-read the strip. It runs AFTER the render rather
  // than before, because a Graph sweep is seconds and the scoreboard must not wait on it.
  const verifySends = useCallback(async () => {
    setVerifying(true);
    try {
      await callWinback('confirm-sends', { days: 2 });
      const r = await callWinback('team');
      setTeam(r.team || []);
    } catch (_) {
      // Never surfaced as an error. A sweep that could not run leaves every send reading
      // "checking", which is the honest state and is what the strip already renders.
    } finally { setVerifying(false); }
  }, []);
  // ⚠️ verifySends MUST BE DECLARED ABOVE THE useEffect THAT NAMES IT. A dependency array is
  // evaluated AT RENDER, so with the const below it every render threw "Cannot access
  // 'verifySends' before initialization" and the whole VIP Win-back tab rendered BLANK -
  // from 2026-09-18 18:13 until 2026-09-21. `npm run build` compiles it without a murmur;
  // only rendering the component finds it (scripts/test-render-panels.js does exactly that).
  useEffect(() => { loadCohorts(); }, [loadCohorts]);
  // One sweep per visit. Not on an interval - a Graph read per tick against three colleagues'
  // mailboxes would be a real cost for a number that only moves when somebody sends.
  useEffect(() => { verifySends(); }, [verifySends]);
  useEffect(() => { setPicked(new Set()); loadQueue(sel, scope, held); }, [sel, scope, held, loadQueue]);

  // ⚠️ ASKS THE SERVER BEFORE OPENING THE COMPOSER. The list may be hours old, and the three
  // things that make a send wrong all change after it is drawn.
  const startEmail = async (c) => {
    try {
      const r = await callWinback('check', { customerId: c.customerId });
      if (!r.ok) { setBlocked({ name: c.name, reason: r.reason }); return; }
      setComposing({ ...c, segmentKey: `winback:${sel.year}:${sel.reason}` });
    } catch (e) { setBlocked({ name: c.name, reason: e.message }); }
  };

  const onSent = (customerId) => {
    setQueue((q) => q.filter((x) => x.customerId !== customerId));
    setCap((c) => (c ? { ...c, used: c.used + 1, left: Math.max(0, c.left - 1) } : c));
    loadCohorts();
  };


  const refreshAll = () => { loadCohorts(); loadQueue(sel, scope, held); verifySends(); };

  const move = async (toAdminId) => {
    const ids = [...picked];
    if (!ids.length) return;
    try {
      const r = await callWinback('reassign', { customerIds: ids, toAdminId });
      setMoveMsg(r.moved + (r.moved === 1 ? ' customer' : ' customers') + ' moved to ' + firstName(r.to) + '.');
      setPicked(new Set());
      setTeam(r.team || []);
      loadQueue(sel, scope, held);
    } catch (e) { setMoveMsg(e.message); }
  };

  const takeBack = async () => {
    const ids = [...picked];
    if (!ids.length) return;
    try {
      const r = await callWinback('unassign', { customerIds: ids });
      setMoveMsg(r.cleared + ' put back in the unassigned pool.');
      setPicked(new Set());
      setTeam(r.team || []);
      loadQueue(sel, scope, held);
    } catch (e) { setMoveMsg(e.message); }
  };

  const cover = async (fromAdminId, toAdminId) => {
    try {
      const r = await callWinback('handover', { fromAdminId, toAdminId });
      setMoveMsg(r.moved
        ? r.moved + ' moved to ' + firstName(r.to) + '.'
        : 'They have nothing outstanding to move.');
      setTeam(r.team || []);
      loadQueue(sel, scope, held);
    } catch (e) { setMoveMsg(e.message); }
  };

  const years = useMemo(() => [...new Set(cohorts.map((c) => c.year))].sort((a, b) => b - a), [cohorts]);
  const forYear = useMemo(() => cohorts.filter((c) => c.year === sel.year), [cohorts, sel.year]);
  const current = forYear.find((c) => c.reason === sel.reason);
  const paidBucket = cohorts.find((c) => c.reason === 'charged_ok');
  const isLead = !!(me && me.isLead);
  const working = team.filter((t) => t.open > 0 || t.dealtToday > 0 || t.sentToday > 0);
  const teamCap = cap ? cap.cap * Math.max(1, working.length || 0) : null;

  if (loading) return <div style={{ padding: 28, color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading the win-back list...</div>;

  const scopeChip = (active) => ({
    ...BTN(active ? '#fff3ed' : '#fff', active ? 'var(--orange)' : 'var(--gray-200)', active ? 'var(--orange)' : 'var(--gray-600)'),
  });

  return (
    <div style={{ padding: '4px 0 40px' }}>
      {err && <div style={{ background: '#fff5f5', border: '1px solid #fbb', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: '#a12020', marginBottom: 12 }}>{err}</div>}

      {/* Today's allowance. ⚠️ THE BIG NUMBER IS THE READER'S OWN FIFTY, not the team's total -
          a rep needs to know what SHE has left, and reading a shared number as hers is precisely
          the confusion the per-rep cap exists to end. The team figure sits beside it, smaller. */}
      {cap && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: cap.left > 0 ? '#f7fafc' : '#fff8f3', border: '1px solid ' + (cap.left > 0 ? 'var(--gray-200)' : 'var(--orange)'), borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 13 }}>
            <b style={{ color: cap.left > 0 ? 'var(--navy)' : 'var(--orange)', fontSize: 15 }}>{cap.used} of {cap.cap}</b>
            <span style={{ color: 'var(--gray-600)' }}> sent by you today</span>
          </div>
          {cap.teamUsed > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-600)' }}>
              Team {cap.teamUsed}{teamCap ? ' of ' + teamCap : ''} today
            </div>
          )}
          {/* The reader's OWN confirmation, so a rep sees her own gap without needing the lead
              view. Silent when there is nothing to say - a green tick on every send all day is
              noise, and this line exists for the day it is not green. */}
          {mine && mine.sentToday > 0 && (mine.missingToday > 0 || mine.checkingToday > 0) && (
            <div style={{ fontSize: 12.5, fontWeight: mine.missingToday > 0 ? 700 : 400,
              color: mine.missingToday > 0 ? 'var(--orange)' : 'var(--gray-600)' }}>
              {mine.missingToday > 0
                ? mine.missingToday + ' of yours did not reach Outlook'
                : 'checking ' + mine.checkingToday + ' with Outlook'}
            </div>
          )}
          {verifying && <span style={{ fontSize: 11.5, color: 'var(--gray-400)' }}>checking Sent Items…</span>}
          {cap.left <= 0 && <span style={{ fontSize: 12.5, color: 'var(--orange)', fontWeight: 600 }}>That is your {cap.cap} for today. The rest of your list keeps until tomorrow.</span>}
          {/* Covering for somebody who is out: the lead raises her own limit for today only. */}
          {me && me.isLead && (
            <button
              onClick={async () => {
                const v = window.prompt('How many can you send today? (Back to ' + (cap.baseCap || 50) + ' tomorrow.)', String(cap.cap));
                if (v === null || !/^\d+$/.test(v.trim())) return;
                try { const r = await callWinback('cap-today', { cap: Number(v) }); setCap(r.cap); } catch (e) { setErr(e.message); }
              }}
              style={{ border: 'none', background: 'none', color: 'var(--orange)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
            >{cap.cap !== cap.baseCap && cap.baseCap != null ? 'today only - change' : 'covering for someone? raise today'}</button>
          )}
          <div style={{ flex: 1 }} />
          {stats && stats.total.emailed > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-600)' }}>
              {stats.total.won} back of {stats.total.emailed} emailed
              {stats.total.perHundred != null
                ? <b style={{ color: 'var(--navy)' }}> &middot; {stats.total.perHundred} per 100</b>
                : <span style={{ color: 'var(--gray-400)' }}> &middot; too few to rate yet</span>}
            </div>
          )}
          <button onClick={refreshAll} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      )}

      {/* The lead's read of the day. Rachael runs this department, so she sees all three at once
          and can move work without going near anybody's screen. */}
      {isLead && (
        <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: working.length ? 10 : 0 }}>
            <Users size={15} color="var(--navy)" />
            <b style={{ fontSize: 13, color: 'var(--navy)' }}>Today across the team</b>
            <div style={{ flex: 1 }} />
            {current && current.mailable && (
              <button onClick={() => setDealing(true)} style={BTN('var(--navy)', 'var(--navy)', '#fff')}>
                <Shuffle size={12} /> Hand out today's batch
              </button>
            )}
          </div>

          {working.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)' }}>
              Nothing handed out yet. Everyone is working from the unassigned pool, which is fine
              {current && current.mailable ? ', or hand out today’s batch to split it three ways.' : '.'}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {working.map((t) => (
                <div key={t.adminId} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '9px 12px', minWidth: 190, background: '#fcfdfe' }}>
                  <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13.5 }}>{firstName(t.name)}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--gray-700)', marginTop: 3 }}>
                    <b style={{ color: t.sentToday >= (cap ? cap.cap : 50) ? 'var(--orange)' : 'var(--navy)' }}>{t.sentToday}</b> sent today
                    <span style={{ color: 'var(--gray-500)' }}> &middot; {t.open} still to go</span>
                  </div>
                  {/* ⚠️ THE LINE ABOVE COUNTS CLICKS. This one counts what Exchange can actually
                      show us, and the two are not the same number - three of Linda's fifty on
                      2026-09-18 were clicked and never left Outlook. A rep whose sends are all
                      confirmed gets a quiet grey line; one with a gap gets orange, because that
                      is a customer who was promised an offer and did not get one. */}
                  {t.sentToday > 0 && (
                    <div style={{ fontSize: 11.5, marginTop: 3,
                      color: t.missingToday > 0 ? 'var(--orange)' : 'var(--gray-500)',
                      fontWeight: t.missingToday > 0 ? 700 : 400 }}>
                      {t.missingToday > 0 && <AlertTriangle size={11} style={{ verticalAlign: -1, marginRight: 3 }} />}
                      {t.confirmedToday} confirmed sent
                      {t.missingToday > 0 ? ' · ' + t.missingToday + ' we cannot find' : ''}
                      {t.checkingToday > 0 ? ' · ' + t.checkingToday + ' still checking' : ''}
                    </div>
                  )}
                  <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2 }}>
                    {t.dealtToday > 0 ? t.dealtToday + ' handed out today' : 'nothing new today'}
                    {t.won > 0 ? ' · ' + t.won + ' won back' : ''}
                    {/* A customer who rejoined on their own is neither open nor done. Shown so
                        the count on this card and the rows on screen agree. */}
                    {t.rejoined > 0 ? ' · ' + t.rejoined + ' rejoined on their own' : ''}
                  </div>
                  {t.open > 0 && (
                    <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Out today? Give to</span>
                      {reps.filter((r) => String(r.adminId) !== String(t.adminId) && r.recentSends > 0).map((r) => (
                        <button key={r.adminId} onClick={() => cover(t.adminId, r.adminId)}
                          style={{ ...BTN('#fff', 'var(--gray-300)', 'var(--gray-700)'), padding: '3px 8px', fontSize: 11 }}>
                          {firstName(r.name)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {moveMsg && <div style={{ fontSize: 12.5, color: 'var(--navy)', marginTop: 9 }}>{moveMsg}</div>}
        </div>
      )}

      <OutreachDeliverability kind="winback" title="Are the win-back emails landing?" />

      {/* The money we may already owe somebody. Shown regardless of which segment is selected. */}
      {paidBucket && paidBucket.members > 0 && (
        <div style={{ background: '#fffaf0', border: '1px solid #f0b429', borderRadius: 8, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10 }}>
          <AlertTriangle size={17} color="#b7791f" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: 'var(--gray-800)', lineHeight: 1.55 }}>
            <b style={{ color: '#975a16' }}>{paidBucket.members} members paid and still show lapsed.</b>{' '}
            They were charged in the 31 March renewal batch, Authorize.Net shows no refund, and they are not a member. Check for a chargeback first - Authorize.Net cannot see those, and on 30 September 45 of 48 in this group turned out to be chargebacks.
            Members who were refunded are in their own group and are not counted here. <b>They cannot be emailed this offer</b>
            {' '}and the server refuses it, because telling somebody who has already paid that they can come back free is
            the wrong message. This needs a refund or a reinstatement, not a win-back.
          </div>
        </div>
      )}

      {/* Segments: the year they fell off, then why. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {years.map((y) => (
          <button key={y} onClick={() => setSel({ year: y, reason: y === 2026 ? DEFAULT_2026_REASON : 'unknown' })}
            style={{ ...BTN(sel.year === y ? 'var(--navy)' : '#fff', sel.year === y ? 'var(--navy)' : 'var(--gray-300)', sel.year === y ? '#fff' : 'var(--gray-700)') }}>
            Lapsed {y}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {forYear.map((c) => (
          <button key={c.reason} onClick={() => setSel({ year: c.year, reason: c.reason })}
            title={c.mailable ? '' : 'These members paid. They cannot be emailed this offer.'}
            style={{
              ...BTN(sel.reason === c.reason ? '#fff3ed' : '#fff', sel.reason === c.reason ? 'var(--orange)' : 'var(--gray-200)', sel.reason === c.reason ? 'var(--orange)' : 'var(--gray-600)'),
              opacity: c.mailable ? 1 : 0.75,
            }}>
            {!c.mailable && <AlertTriangle size={11} />}
            {c.reasonLabel}
            <b style={{ marginLeft: 2 }}>{c.remaining}</b>
            {c.alreadyEmailed > 0 && <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>({c.alreadyEmailed} done)</span>}
          </button>
        ))}
      </div>

      {/* Whose list am I looking at. A rep gets her own and the unassigned pool; the lead also
          gets everybody, and each person by name. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)' }}>Showing</span>
        <button onClick={() => setScope('mine')} style={scopeChip(scope === 'mine')}>
          My batch{myOpen > 0 ? ' ' + myOpen : ''}
        </button>
        <button onClick={() => setScope('unassigned')} style={scopeChip(scope === 'unassigned')}>Unassigned</button>
        <button onClick={() => setScope('sent')} style={scopeChip(scope === 'sent')}>
          <Search size={11} /> Already emailed
        </button>
        {/* The second note to the ones who never answered. Its own view because it is its own
            list: each rep's own first emails, whatever year or reason pill is selected above. */}
        <button onClick={() => setScope('followup')} style={scopeChip(scope === 'followup')}>
          <Mail size={11} /> Follow-ups
        </button>
        {/* The routing rule, made visible. Ashley, 2026-09-17: customers were getting the free
            win-back on top of a priced Delivered-by-day invite, and taking the free one. These
            are the ones now being left to that tab - shown rather than silently dropped, so
            Rachael can see who they are and argue with the rule if she wants to. */}
        {current && current.mailable && current.held > 0 && (
          <button
            onClick={() => setHeld(held === 'only' ? 'exclude' : 'only')}
            style={scopeChip(held === 'only')}
            title="Left to Delivered by day: already invited from there, or ordered recently"
          >
            Going to Delivered by day {current.held}
          </button>
        )}
        {isLead && <button onClick={() => setScope('all')} style={scopeChip(scope === 'all')}>Everyone</button>}
        {isLead && working.filter((t) => me && String(t.adminId) !== String(me.adminId)).map((t) => (
          <button key={t.adminId} onClick={() => setScope(t.adminId)} style={scopeChip(String(scope) === String(t.adminId))}>
            {firstName(t.name)}
          </button>
        ))}
      </div>

      {scope !== 'followup' && current && !current.mailable && (
        <div style={{ fontSize: 13, color: '#975a16', background: '#fffaf0', border: '1px solid #f0b429', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
          This group is here so it can be put right, not emailed. There is no send button on these rows.
        </div>
      )}

      {held === 'only' && (
        <div style={{ fontSize: 13, color: 'var(--navy)', background: '#eef4ff', border: '1px solid #c7d7fe', borderRadius: 6, padding: '10px 12px', marginBottom: 12, lineHeight: 1.6 }}>
          <b>These are not getting the free offer.</b> They have either already had a VIP offer
          from Delivered by day, or they have ordered recently, so that tab picks them up on their
          next box. Sending a free year on top of a priced offer undercuts it, and the customer
          takes the free one.
          {current && current.heldInvited > 0 && current.heldRecent > 0 && (
            <> {current.heldInvited} already invited, {current.heldRecent} ordered recently.</>
          )}
          {' '}There is no send button on these rows.
        </div>
      )}

      {/* Move the ticked rows. Lead only, because these write who owns the work. */}
      {isLead && picked.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: '#fff3ed', border: '1px solid var(--orange)', borderRadius: 8, padding: '9px 12px', marginBottom: 12 }}>
          <b style={{ fontSize: 13, color: 'var(--navy)' }}>{picked.size} ticked</b>
          <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>Give to</span>
          {reps.filter((r) => r.recentSends > 0).map((r) => (
            <button key={r.adminId} onClick={() => move(r.adminId)} style={BTN('#fff', 'var(--gray-300)', 'var(--navy)')}>
              {firstName(r.name)}
            </button>
          ))}
          <button onClick={takeBack} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-600)')}>Put back in the pool</button>
          <div style={{ flex: 1 }} />
          <button onClick={() => setPicked(new Set())} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-600)')}>Clear</button>
        </div>
      )}

      {/* Put the whole visible queue in her Outlook drafts in one go. Only where a send is
          actually allowed: the unmailable groups and the held-back pool have no send button
          on their rows, and a bulk button that ignored that would undercut Delivered by day. */}
      {scope !== 'sent' && scope !== 'followup' && queue.length > 0 && current && current.mailable && held !== 'only' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#eef4ff', border: '1px solid #c7d7fe', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          <button
            onClick={() => draftBatch(queue)}
            disabled={!!drafting}
            style={{ ...BTN('var(--navy)', 'var(--navy)', '#fff'), opacity: drafting ? 0.6 : 1 }}
          >
            {drafting
              ? `Writing ${drafting.done} of ${drafting.total}...`
              : `Put ${Math.min(DRAFT_BATCH, queue.length)} in my Outlook drafts`}
          </button>
          <span style={{ fontSize: 12.5, color: 'var(--gray-600)', lineHeight: 1.5 }}>
            They appear in Outlook already written. Read each one and press Send. Nothing is sent for you.
          </span>
        </div>
      )}

      {draftMsg && (
        <div style={{ fontSize: 13, lineHeight: 1.6, background: draftMsg.error ? '#fff5f5' : '#f0fff4', border: '1px solid ' + (draftMsg.error ? '#fc8181' : '#9ae6b4'), borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            {draftMsg.error ? (
              <b style={{ color: '#c53030' }}>Could not write the drafts: {draftMsg.error}</b>
            ) : (
              <>
                <b style={{ color: 'var(--navy)' }}>
                  {draftMsg.created} {draftMsg.created === 1 ? 'draft is' : 'drafts are'} in your Outlook
                  {draftMsg.mailbox ? ' (' + draftMsg.mailbox + ')' : ''}.
                </b>{' '}
                Open Outlook, read each one and press Send.
                {/* Named, never silently dropped: a customer who was skipped is one somebody
                    may still need to deal with. */}
                {(draftMsg.blocked || []).length > 0 && (
                  <div style={{ marginTop: 6, color: 'var(--gray-600)' }}>
                    {draftMsg.blocked.length} skipped: {draftMsg.blocked.slice(0, 3).map((b) => b.reason).join('; ')}
                    {draftMsg.blocked.length > 3 ? ' and others' : ''}
                  </div>
                )}
                {(draftMsg.failed || []).length > 0 && (
                  <div style={{ marginTop: 6, color: '#c53030' }}>
                    {draftMsg.failed.length} could not be written to Outlook. Try those again.
                  </div>
                )}
              </>
            )}
          <button onClick={() => setDraftMsg(null)} style={{ ...BTN('#fff', 'var(--gray-300)', 'var(--gray-600)'), marginLeft: 10 }}>Dismiss</button>
        </div>
      )}

      {/* The queue, or - for a reply that needs tracing back - everyone already emailed. */}
      {scope === 'sent' ? (
        <SentLookup />
      ) : scope === 'followup' ? (
        <VipWinbackFollowup rep={rep} />
      ) : listing ? (
        <div style={{ padding: 24, color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading...</div>
      ) : queue.length === 0 ? (
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13.5 }}>
          {scope === 'mine'
            ? 'Nothing is handed to you in this group. Try Unassigned, where anybody can pick one up.'
            : 'Nobody left in this group. Pick another year or another reason.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="orders-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                {isLead && <th style={{ width: 28, padding: '8px 6px' }} />}
                {['Customer', 'Member since', 'Lapsed', 'Orders', 'Lifetime', 'Last order', 'Why', ''].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= 3 && i <= 5 ? 'right' : 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {queue.map((c) => (
                <tr key={c.customerId} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  {isLead && (
                    <td style={{ padding: '9px 6px' }}>
                      <input type="checkbox" checked={picked.has(String(c.customerId))}
                        onChange={() => setPicked((s) => {
                          const n = new Set(s); const k = String(c.customerId);
                          if (n.has(k)) n.delete(k); else n.add(k); return n;
                        })} />
                    </td>
                  )}
                  <td style={{ padding: '9px 10px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{c.email || 'no email on file'}</div>
                  </td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                    {fmtDate(c.joined)}{c.renewals > 0 && <span style={{ color: 'var(--gray-400)' }}> &middot; {c.renewals} renewals</span>}
                  </td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{fmtDate(c.expired)}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right' }}>{c.orders}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--navy)' }}>{money(c.revenue)}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{fmtDate(c.lastOrder)}</td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', fontSize: 12 }}>
                    {c.reasonLabel}{c.errorCode ? <span style={{ color: 'var(--gray-400)' }}> ({c.errorCode})</span> : ''}
                    {/* Whose it is, wherever you are looking from. A rep on the unassigned pool
                        who picks up one of Linda's is not blocked, she is just told. */}
                    {c.ownerName && (
                      <div style={{ fontSize: 11, color: me && String(c.ownerAdminId) === String(me.adminId) ? 'var(--orange)' : 'var(--gray-400)', fontWeight: 600 }}>
                        {me && String(c.ownerAdminId) === String(me.adminId) ? 'yours' : firstName(c.ownerName) + '’s'}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {c.emailedOn ? (
                      <span style={{ fontSize: 11.5, color: 'var(--green,#2f855a)', fontWeight: 600 }}><Check size={11} /> {fmtDate(c.emailedOn)}</span>
                    ) : c.heldReason ? (
                      /* Routed to Delivered by day. The reason replaces the button rather than
                         sitting beside a disabled one, so there is nothing to click and nothing
                         to wonder about. */
                      <span style={{ fontSize: 11.5, color: 'var(--navy)', fontWeight: 600, whiteSpace: 'normal', display: 'inline-block', maxWidth: 230, textAlign: 'right', lineHeight: 1.45 }}>
                        {c.heldLabel}
                        {c.invitedPrice != null && c.heldReason === 'invited' && (
                          <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}>
                            {' '}({c.invitedPrice > 0 ? '$' + c.invitedPrice.toFixed(2) : 'free'})
                          </span>
                        )}
                      </span>
                    ) : c.mailable && c.email ? (
                      <>
                        <button onClick={() => startEmail(c)} style={BTN('#fff3ed', 'var(--orange)', 'var(--orange)')}>
                          <Mail size={12} /> Email offer
                        </button>
                        {/* Quiet on purpose: sending is the job and this is the exception, so it
                            reads as a link beside the button rather than a rival to it. */}
                        <button
                          onClick={() => skipCustomer(c)}
                          title="Take this customer off the win-back queue for good"
                          style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', marginLeft: 8, padding: 0, fontFamily: 'inherit' }}
                        >
                          Not worth pursuing
                        </button>
                        {/* Stronger than a skip: stops the newsletter and texts too, on every list. */}
                        <button
                          onClick={() => setDncFor(c)}
                          title="Stop ALL contact with this customer - offers, newsletter and texts"
                          style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', marginLeft: 8, padding: 0, fontFamily: 'inherit' }}
                        >
                          Do not contact
                        </button>
                      </>
                    ) : c.reason === 'charged_ok' ? (
                      <button onClick={() => flagChargeback(c)} title="They disputed the charge with their bank"
                        style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>
                        Mark chargeback
                      </button>
                    ) : c.reason === 'chargeback' ? (
                      <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
                        charged back
                        <button onClick={() => clearChargeback(c)}
                          title="The bank reversed the chargeback in our favour"
                          style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', marginLeft: 8, padding: 0, fontFamily: 'inherit' }}>
                          Mark won (reversed)
                        </button>
                      </span>
                    ) : (
                      <span style={{ fontSize: 11.5, color: 'var(--gray-400)' }}>{c.email ? 'not mailable' : 'no email'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dncFor && (
        <DncModal
          customer={{ customerId: dncFor.customerId, name: dncFor.name, email: dncFor.email }}
          source="winback"
          onClose={() => setDncFor(null)}
          onDone={() => { setDncFor(null); loadQueue(sel, scope, held); loadCohorts(); }}
        />
      )}

      {blocked && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onMouseDown={() => setBlocked(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 22, maxWidth: 460, boxShadow: '0 18px 40px rgba(16,24,40,0.3)' }} onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <AlertTriangle size={20} color="#b7791f" style={{ flexShrink: 0 }} />
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>Hold off on {blocked.name}</div>
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--gray-700)', lineHeight: 1.6, marginBottom: 16 }}>{blocked.reason}</div>
            <button onClick={() => { setBlocked(null); loadQueue(sel, scope, held); }} style={{ ...BTN('var(--navy)', 'var(--navy)', '#fff'), padding: '8px 16px', fontSize: 13 }}>Got it</button>
          </div>
        </div>
      )}

      {dealing && (
        <DealPanel
          reps={reps} team={team} cap={cap ? cap.cap : 50} segment={sel}
          onClose={() => setDealing(false)}
          onDone={(t) => { if (t) setTeam(t); loadQueue(sel, scope, held); loadCohorts(); }}
        />
      )}

      {composing && rep && (
        <WinbackModal customer={composing} rep={rep} onClose={() => setComposing(null)} onSent={onSent} />
      )}
    </div>
  );
}
